import React, { useEffect, useRef, useState, useId } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { X, Send, CheckCircle, User, Mail, Phone, MessageSquare } from "lucide-react";
import CTAButton from "@/components/ui/CTAButton";
import Field, { FormError } from "@/components/ui/Field";
import { API } from "@/lib/api";

const SELECTOR_FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/**
 * Modal de inscripción a un curso, evento o retiro.
 *
 * La versión anterior era, con diferencia, el peor punto de accesibilidad de la
 * web pública. No tenía `role="dialog"` ni `aria-modal`, no atrapaba el foco
 * —que se quedaba en el botón de detrás del velo—, no se cerraba con Escape, no
 * bloqueaba el desplazamiento de la página de fondo, su botón de cerrar
 * contenía solo un icono y por tanto no tenía nombre accesible, y sus etiquetas
 * no estaban asociadas a los campos.
 *
 * Además, un clic en el velo lo cerraba siempre: bastaba con soltar el ratón
 * fuera del cuadro mientras se seleccionaba texto para perder todo lo escrito.
 * Ahora, si hay algo escrito, el velo deja de cerrar; queda Escape y los dos
 * botones, que son gestos deliberados.
 */
export default function EnrollmentModal({ itemId, itemTitle, itemType, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "", honeypot: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const dialogRef = useRef(null);
  const disparadorRef = useRef(null);
  const tituloId = useId();

  const sucio = Object.values(form).some(v => v.trim() !== "");

  const endpoint =
    {
      evento: `/events/${itemId}/enroll`,
      curso: `/courses/${itemId}/enroll`,
      retiro: `/retreats/${itemId}/enroll`,
    }[itemType] || `/events/${itemId}/enroll`;

  const typeLabel =
    {
      evento: "Inscripción al evento",
      curso: "Inscripción al curso",
      retiro: "Inscripción al retiro",
    }[itemType] || "Inscripción";

  // Bloqueo del scroll de fondo, gestión del foco y trampa de tabulación.
  useEffect(() => {
    disparadorRef.current = document.activeElement;

    const previo = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // El foco entra en el cuadro; si no, se queda detrás del velo y el usuario
    // de teclado tabula por una página que no puede ver.
    const primero = dialogRef.current?.querySelector(SELECTOR_FOCUSABLE);
    primero?.focus();

    const onKeyDown = e => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      const focusables = Array.from(dialogRef.current?.querySelectorAll(SELECTOR_FOCUSABLE) || []).filter(
        el => el.offsetParent !== null
      );
      if (focusables.length === 0) return;

      const primeroFoco = focusables[0];
      const ultimoFoco = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === primeroFoco) {
        e.preventDefault();
        ultimoFoco.focus();
      } else if (!e.shiftKey && document.activeElement === ultimoFoco) {
        e.preventDefault();
        primeroFoco.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previo;
      document.removeEventListener("keydown", onKeyDown);
      // Al cerrar, el foco vuelve al botón que abrió el modal.
      if (disparadorRef.current instanceof HTMLElement) disparadorRef.current.focus();
    };
  }, [onClose]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError("El nombre y el email son obligatorios.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}${endpoint}`, form, { withCredentials: true });
      setSuccess(true);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Ha ocurrido un error. Por favor, contáctanos directamente en evangelizaciondigital@diocesisdehuelva.es"
      );
    } finally {
      setLoading(false);
    }
  };

  // Portal a document.body: este modal se abre desde Eventos.js/Formacion.js,
  // dentro de <PageTransition>. Ese envoltorio deja un `transform` residual
  // tras su animación de entrada (animation-fill-mode: both), lo que lo
  // convierte en el bloque de contención de cualquier `position:fixed`
  // anidado dentro — el modal se centraba respecto a ese div y no respecto al
  // viewport, así que en cuanto se abría con la página desplazada aparecía
  // muy por debajo de lo visible, con el formulario y el botón de enviar
  // fuera de pantalla. Mismo problema y mismo arreglo que la barra flotante
  // de PageHero.js.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn"
      onClick={() => { if (!sucio) onClose(); }}
      data-testid="enrollment-modal-overlay"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        className="bg-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto animate-modal-in"
        onClick={e => e.stopPropagation()}
        data-testid="enrollment-modal"
      >
        {/* Cabecera */}
        <div className="bg-gradient-to-br from-[#0a1628] to-[#1868C2] p-6 text-white">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/70 mb-1">{typeLabel}</p>
              <h2 id={tituloId} className="font-heading font-bold text-lg leading-tight">
                {itemTitle}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors duration-fast flex-shrink-0 focus-ring-light"
            >
              <X className="w-4 h-4" aria-hidden="true" />
              <span className="sr-only">Cerrar la inscripción</span>
            </button>
          </div>
        </div>

        {success ? (
          <div className="p-8 text-center" role="status">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" aria-hidden="true" />
            </div>
            <h3 className="font-heading font-bold text-lg mb-2">¡Inscripción enviada!</h3>
            <p className="text-sm text-muted-foreground mb-2">
              Hemos recibido tu solicitud y recibirás un email de confirmación en breve.
            </p>
            <p className="text-xs text-muted-foreground">
              También hemos notificado a la delegación. Nos pondremos en contacto contigo pronto.
            </p>
            <CTAButton onClick={onClose} className="mt-6 font-bold">
              Cerrar
            </CTAButton>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4" noValidate>
            <p className="text-sm text-muted-foreground">
              Rellena tus datos y nos pondremos en contacto contigo para confirmar tu plaza.
            </p>

            <Field
              label={<><User className="w-3.5 h-3.5 inline mr-1" aria-hidden="true" />Nombre completo</>}
              required
              data-testid="enroll-name"
              type="text"
              autoComplete="name"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Tu nombre y apellidos"
            />

            <Field
              label={<><Mail className="w-3.5 h-3.5 inline mr-1" aria-hidden="true" />Email</>}
              required
              data-testid="enroll-email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="tu@email.com"
            />

            <Field
              label={<><Phone className="w-3.5 h-3.5 inline mr-1" aria-hidden="true" />Teléfono</>}
              data-testid="enroll-phone"
              type="tel"
              autoComplete="tel"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="600 000 000 (opcional)"
            />

            <Field
              label={<><MessageSquare className="w-3.5 h-3.5 inline mr-1" aria-hidden="true" />Notas o comentarios</>}
              as="textarea"
              data-testid="enroll-notes"
              rows={3}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="¿Alguna pregunta o información adicional? (opcional)"
            />

            {/* Señuelo anti-spam: oculto también para tecnología asistiva. */}
            <input
              type="text"
              name="honeypot"
              value={form.honeypot}
              onChange={e => setForm(f => ({ ...f, honeypot: e.target.value }))}
              style={{ display: "none" }}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <FormError data-testid="enroll-error">{error}</FormError>

            <div className="flex gap-3 pt-2">
              <CTAButton type="submit" disabled={loading} data-testid="enroll-submit" className="flex-1 font-bold">
                {loading ? (
                  "Enviando..."
                ) : (
                  <>
                    <Send className="w-4 h-4" aria-hidden="true" /> Enviar inscripción
                  </>
                )}
              </CTAButton>
              <CTAButton type="button" onClick={onClose} variant="outline">
                Cancelar
              </CTAButton>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
